// Hooks and Library Utilities
import React, { useEffect, useRef, useState } from "react";
import Select from "react-select";

// components
import ErrorMessage from "../../../../../Shared/error-message/errorMessage";
import InputField from "../../Shared/inputField/inputField";

// Styles
import Styles from "../../Shared/sharedStyles.module.scss";
import dropDownStyles from "../../../../../Shared/dropDownStyles";
import {
  buttonStyles,
  StyledButton,
} from "../../../../../Shared/button/button";

// constants
import { days, months } from "../../../../../constants/data";

// Functions
import { getYears } from "../../../../../utils/getYears";
import { validDLNumber, validNameCheck } from "../../../../../utils/validation";
import { IdsGenerator } from "../../../../../utils/idGenerator";

function DlServicePopup({ handleResponse, name }: any) {
  const [day, setDay] = useState("");
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");
  const [isDisabled, setIsDisabled] = useState(true);
  const [loading, setLoading] = useState(false);
  const nameMatchRef = useRef<any>("");
  const [isNameMatchEnabled, setIsNameMatchEnabled] = useState(false);

  const errorObject = {
    dlNumberError: false,
    consentError: false,
    dobDayError: false,
    dobMonthError: false,
    dobYearError: false,
    nameToMatchError: false,
  };
  const [error, setError] = useState({ ...errorObject });
  const [isChecked, setIsChecked] = useState(false);

  const dlNumberRef = useRef<any>("");
  const yearOptions = getYears(1950);

  useEffect(() => {
    if (isNameMatchEnabled) {
      setIsDisabled(true);
    }
    if (!isNameMatchEnabled) {
      if (dlNumberRef.current.value) {
        setIsDisabled(false);
      }
    }
  }, [isNameMatchEnabled]);

  function isValid() {
    if (validDLNumber(dlNumberRef.current.value.trim().toUpperCase())) {
      setError(errorObject);
      return setIsDisabled(false);
    }
    setError(() => ({
      ...error,
      dlNumberError: true,
    }));
    return setIsDisabled(true);
  }

  function isValidNameMatch() {
    setError({ ...errorObject });
    if (
      validDLNumber(dlNumberRef.current.value.trim().toUpperCase()) &&
      validNameCheck(nameMatchRef.current.value)
    ) {
      setError(errorObject);
      return setIsDisabled(false);
    }
    if (!validNameCheck(nameMatchRef.current.value)) {
      setError(() => ({
        ...error,
        nameToMatchError: true,
      }));
    }
    return setIsDisabled(true);
  }

  function handleCheck() {
    const dlNumber = dlNumberRef.current.value.trim().toUpperCase();
    const dobNeed = ["AP", "TS"];
    const stateNeedDob = dobNeed.some((state) => dlNumber.includes(state));

    if (isNameMatchEnabled && nameMatchRef.current.value === "") {
      setError(() => ({
        ...error,
        nameToMatchError: true,
      }));
      return setIsDisabled(true);
    }

    if (!day && stateNeedDob) {
      setError(() => ({
        ...error,
        dobDayError: true,
      }));
      return false;
    }
    if (!month && stateNeedDob) {
      setError(() => ({
        ...error,
        dobMonthError: true,
      }));
      return false;
    }
    if (!year && stateNeedDob) {
      setError(() => ({
        ...error,
        dobYearError: true,
      }));
      return false;
    } else if (!isChecked) {
      setError(() => ({
        ...error,
        consentError: true,
      }));
      return false;
    }

    const dob = `${day}-${month}-${year}`;
    const consent = isChecked === true ? "Y" : "N";
    setLoading(true);
    if (nameMatchRef && nameMatchRef.current && nameMatchRef.current.value) {
      const nameToMatch = nameMatchRef.current.value;
      handleResponse({ dlNumber, nameToMatch, dob, consent }, setLoading);
    } else if (!isNameMatchEnabled) {
      handleResponse({ dlNumber, dob, consent }, setLoading);
    }
  }
  return (
    <div className={`${Styles.content}`}>
      <div className={`${Styles.infoForm} pl-2`}>
        <hr />
        <p className="mt-2">Fill in the below details to run verification</p>
        <div className="d-flex flex-column">
          <div className="d-flex flex-column mb-4 ">
            <label className={`${Styles.label} flex-grow-1`}>DL Number</label>
            <InputField
              id={IdsGenerator({
                prefix: "driving licence advance",
                id: "change",
                suffix: "dl number",
              })}
              onChange={isValid}
              maxLength={20}
              minLength={10}
              inputRef={dlNumberRef}
              isError={error.dlNumberError}
              purpose="forDl"
            />
            {error.dlNumberError && (
              <ErrorMessage>Please enter a valid DL number</ErrorMessage>
            )}
          </div>
          <div>
            <label className={`${Styles.label}`}>DOB</label>
            <div className="d-flex mr-1">
              <div className="mr-2 d-flex gap-2">
                <div className="d-flex flex-column">
                  <Select
                    id={IdsGenerator({
                      prefix: "driving licence advance",
                      id: "select",
                      suffix: "dl date",
                    })}
                    onChange={(e: any) => setDay(e.value)}
                    options={days}
                    styles={
                      !error.dobDayError
                        ? dropDownStyles.customStylesForDays
                        : dropDownStyles.customStylesForDaysError
                    }
                    placeholder="DD"
                  />
                </div>
                <div className="ml-2 d-flex flex-column">
                  <Select
                    id={IdsGenerator({
                      prefix: "driving licence advance",
                      id: "select",
                      suffix: "dl month",
                    })}
                    onChange={(e: any) => setMonth(e.value)}
                    options={months}
                    styles={
                      !error.dobMonthError
                        ? dropDownStyles.customStylesForMonths
                        : dropDownStyles.customStylesForMonthsError
                    }
                    placeholder="MMM"
                  />
                </div>
                <div className="ml-2 d-flex flex-column">
                  <Select
                    id={IdsGenerator({
                      prefix: "driving licence advance",
                      id: "select",
                      suffix: "dl year",
                    })}
                    onChange={(e: any) => setYear(e.value)}
                    placeholder="YYYY"
                    options={yearOptions}
                    styles={
                      !error.dobYearError
                        ? dropDownStyles.customStylesForYears
                        : dropDownStyles.customStylesForYearsError
                    }
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="pl-4 pb-2 pt-4 d-flex flex-column">
            <div className="d-flex flex-row align-items-center gap-2">
              <input
                id={IdsGenerator({
                  prefix: "driving licence advance",
                  id: "check",
                  suffix: "name to match",
                })}
                data-testid={IdsGenerator({
                  prefix: "driving licence advance",
                  id: "check",
                  suffix: "name to match",
                })}
                type="checkbox"
                className={`${Styles.formCheckInput} `}
                onChange={(e: any) => setIsNameMatchEnabled(e.target.checked)}
              />
              <p className={`${Styles.ackText} mb-0`}>
                is name match required ?
              </p>
            </div>
          </div>
          {isNameMatchEnabled && (
            <div className="d-flex flex-column mb-4 ">
              <label className={`${Styles.label} flex-grow-1`}>
                Name To Match
              </label>
              <InputField
                id={IdsGenerator({
                  prefix: "driving licence advance",
                  id: "change",
                  suffix: "name to match",
                })}
                onChange={isValidNameMatch}
                inputRef={nameMatchRef}
                isError={error.nameToMatchError}
                purpose="forPassport"
                text="lowerCase"
              />
              {error.nameToMatchError && (
                <ErrorMessage>Please enter a valid Name</ErrorMessage>
              )}
            </div>
          )}

          <div className="pt-4 pl-4 pb-4 mt-4 d-flex flex-column">
            <div className="d-flex align-items-center py-0 gap-2">
              <input
                id={IdsGenerator({
                  prefix: "driving licence advance",
                  id: "check",
                  suffix: "consent",
                })}
                data-testid={IdsGenerator({
                  prefix: "driving licence advance",
                  id: "check",
                  suffix: "consent",
                })}
                type="checkbox"
                className={`${Styles.formCheckInput} `}
                onChange={(e: any) => {
                  setIsChecked(e.target.checked);
                  isNameMatchEnabled ? isValidNameMatch() : isValid();
                }}
              />
              <p className={`${Styles.ackText} mb-0`}>
                I hereby agree, to let zoop.one verify my data for verification
              </p>
            </div>
            {error.consentError && (
              <ErrorMessage>Please provide consent!</ErrorMessage>
            )}
          </div>
          <div className={`${Styles.buttonGroup}`}>
            <StyledButton
              id={IdsGenerator({
                prefix: "driving licence advance",
                id: "click",
                suffix: "run verification",
              })}
              hoverStyle={{ ...buttonStyles.primary.hover }}
              disabled={isDisabled}
              isLoading={loading}
              style={{ ...buttonStyles.primary.normal }}
              onClick={() => {
                setTimeout(() => setError({ ...errorObject }), 2000);
                handleCheck();
              }}
            >
              Run Verification
            </StyledButton>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DlServicePopup;
